// The viewer's theme for AI board pages: Auto follows the OS, Light and Dark pin one of the platform's two
// themes. A board page paints itself with the platform's tokens: the root element's data-theme picks their
// light or dark set, and without one the OS setting does. Plain TypeScript, with no React or MUI, so the render
// check and the harness that runs it can import it.
export type BoardTheme = 'auto' | 'light' | 'dark'

export const BOARD_THEMES: BoardTheme[] = ['auto', 'light', 'dark']

// One key per viewer, not per board: the choice is the viewer's, on every board and shared page.
export const BOARD_THEME_KEY = 'sparck_ai_boards_theme'

// The message the parent posts on a frame's port to switch its theme; see THEME_APPLIER.
export const DASHBOARD_THEME_MESSAGE = 'sparck:theme'

// The --bg of each theme, as the platform's tokens.css sets them, for the page around the frames, so a dark
// board doesn't sit in a light strip. Keep them equal to tokens.css.
export const BOARD_THEME_BG = { light: '#F6F8FA', dark: '#0E0F10' }

// The --muted and --line of each theme, as tokens.css sets them, for what the page itself draws on --bg: its own
// words in --muted, which reads at 4.5:1 or more on --bg in both themes, and a block still loading as a --line
// shape. Keep them equal to tokens.css.
export const BOARD_THEME_MUTED = { light: '#5F6972', dark: '#8B979E' }
export const BOARD_THEME_LINE = { light: '#DFE3E8', dark: '#2A2E31' }

export const isBoardTheme = (value: unknown): value is BoardTheme =>
  typeof value === 'string' && (BOARD_THEMES as string[]).includes(value)

// The choice is kept in this browser only. When the storage can't be read or written, it lasts until the page
// is left; Auto is kept as no value at all.
export const storedBoardTheme = (): BoardTheme => {
  try {
    const stored = localStorage.getItem(BOARD_THEME_KEY)

    return isBoardTheme(stored) ? stored : 'auto'
  } catch {
    return 'auto'
  }
}

export const storeBoardTheme = (theme: BoardTheme) => {
  try {
    if (theme === 'auto') {
      localStorage.removeItem(BOARD_THEME_KEY)
    } else {
      localStorage.setItem(BOARD_THEME_KEY, theme)
    }
  } catch {
    // Nothing to do: see storedBoardTheme.
  }
}

// Runs in the frame, as plain ES5 source added by the parent with the page's nonce, so the frame switches
// theme without loading again. Like the runtime, it takes the first port its parent window hands over, and on
// it only {type: 'sparck:theme', theme}: light and dark set the root element's data-theme, auto removes it.
// It ignores every other message and never posts anything.
export const THEME_APPLIER = `(function () {
  'use strict';
  var parentWindow = window.parent, root = document.documentElement, listening = false;
  window.addEventListener('message', function (event) {
    var data = event.data, port = event.ports && event.ports[0];
    if (listening || event.source !== parentWindow || !data || data.type !== 'sparck:port' || !port) return;
    listening = true;
    port.addEventListener('message', function (message) {
      var sent = message.data, theme = sent && sent.type === '${DASHBOARD_THEME_MESSAGE}' ? sent.theme : null;
      if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme);
      else if (theme === 'auto') root.removeAttribute('data-theme');
    });
    port.start();
  });
})();`
